<template>
  <section class="section-two bg-light mt-5 mb-2">
    <div class="container-fluid">
      <div class="row m-3">
        <div class="col-lg-3 mb-4 order-2 order-sm-1 order-md-1">
          <MenuAction/>
        </div>

        <div class="col-lg-9 order-1 order-sm-2 order-md-2">
          <div class="row mb-4">
            <p class="col-md-8 h4">
              <b>Escolha o paciente</b>
            </p>
            <div class="col-md-4 text-sm-end">
              <div class="d-grid gap-2">
                <button class="btn btn-outline-primary" @click="openNewPatientModal">
                  Novo paciente
                </button>
              </div>
            </div>
          </div>

          <div
            v-for="patient in patients"
            :key="patient.id_patient"
            class="card shadow rounded border-0 mb-3 pricing-rates business-rate"
            @click="handlePatientClick(patient)"
            style="cursor: pointer;"
            :title="`Agendar para ${patient.patient_name}`"
          >
            <div class="ribbon ribbon-right ribbon-warning overflow-hidden" v-if="patient.main">
              <span class="text-center d-block shadow small h6">Você</span>
            </div>
            <div class="row">
              <div class="col-md-12 text-center">
                <div class="m-3">
                  <span class="text-primary">
                    <b>{{ patient.patient_name }}</b>
                  </span>
                  <br>
                  <span class="text-primary">
                    {{ patient.patient_full_age }}
                  </span>
                </div>
              </div>
              <div class="col-md-12">
                <div class="mx-3 mb-3 text-center">
                  <div class="d-grid gap-2 d-md-block">
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click.stop="handlePatientClick(patient)"
                      :title="`Agendar para ${patient.patient_name}`"
                    >
                      Agendar <i class="ms-1 fas fa-1x fa-arrow-circle-right"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-primary ms-xl-2"
                      @click.stop="openEditPatientModal(patient)"
                    >
                      Editar <i class="fa fa-edit"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: período de retorno -->
    <div
      class="modal fade"
      id="ModalReturningDeadline"
      tabindex="-1"
      aria-labelledby="ModalReturningDeadlineLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content rounded shadow border-0">
          <div class="modal-header border-bottom">
            <h5 class="modal-title" id="ModalReturningDeadlineLabel">
              <i class="uil uil-exclamation-triangle text-warning me-1"></i>
              Período de retorno
            </h5>
          </div>
          <div class="modal-body" v-if="returningDeadlineContext">
            <div class="alert alert-warning mb-3">
              O CPF <b>{{ returningDeadlineContext.patient.patient_cpf }}</b>
              ({{ returningDeadlineContext.patient.patient_name }}) está no período de <b>RETORNO</b>.
              <br>
              O que você gostaria de fazer a seguir?
            </div>
            <div class="table-responsive">
              <table class="table table-bordered table-sm mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Data e hora</th>
                    <th>Profissional</th>
                    <th>Especialidade</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in returningDeadlineContext.appointments"
                    :key="item.id_sched ?? index"
                  >
                    <td>{{ formatAppointmentDateTime(item) }}</td>
                    <td>{{ item.prof_name }}</td>
                    <td>{{ item.spec_name }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer border-top d-flex flex-column flex-sm-row gap-2">
            <button
              type="button"
              class="btn btn-primary flex-fill"
              @click="handleReturningDeadlineReturn"
            >
              Agendar retorno
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary flex-fill"
              @click="handleReturningDeadlineContinue"
            >
              Continuar e fazer um novo agendamento
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: escolher consulta para retorno -->
    <div
      class="modal fade"
      id="ModalSelectReturnSchedule"
      tabindex="-1"
      aria-labelledby="ModalSelectReturnScheduleLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded shadow border-0">
          <div class="modal-header border-bottom">
            <h5 class="modal-title" id="ModalSelectReturnScheduleLabel">
              Qual consulta usar para o retorno?
            </h5>
          </div>
          <div class="modal-body" v-if="selectReturnContext">
            <p class="text-muted mb-3">
              Selecione a consulta anterior que servirá de base para o retorno.
            </p>
            <div
              v-for="(item, index) in sortedSelectReturnAppointments"
              :key="item.id_sched ?? index"
              class="form-check border rounded p-3 mb-2"
            >
              <input
                class="form-check-input"
                type="radio"
                name="return_schedule"
                :id="`return_schedule_${item.id_sched}`"
                :value="item.id_sched"
                v-model="selectedReturnScheduleId"
              >
              <label class="form-check-label w-100" :for="`return_schedule_${item.id_sched}`">
                <strong>{{ formatAppointmentDateTime(item) }}</strong>
                <br>
                <span class="text-muted">{{ item.prof_name }} — {{ item.spec_name }}</span>
              </label>
            </div>
          </div>
          <div class="modal-footer border-top d-flex flex-column flex-sm-row gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary flex-fill"
              @click="handleSelectReturnBack"
            >
              Voltar
            </button>
            <button
              type="button"
              class="btn btn-primary flex-fill"
              @click="handleSelectReturnConfirm"
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: agendamento duplicado -->
    <div
      class="modal fade"
      id="ModalDuplicateSchedule"
      tabindex="-1"
      aria-labelledby="ModalDuplicateScheduleLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content rounded shadow border-0">
          <div class="modal-header border-bottom">
            <h5 class="modal-title" id="ModalDuplicateScheduleLabel">
              <i class="uil uil-exclamation-triangle text-warning me-1"></i>
              Agendamento existente
            </h5>
          </div>
          <div class="modal-body" v-if="duplicateContext">
            <div class="alert alert-warning mb-3">
              O CPF <b>{{ duplicateContext.patient.patient_cpf }}</b>
              ({{ duplicateContext.patient.patient_name }}) já possui um agendamento
              para esse profissional ou especialidade.
              <br>
              Tem certeza que deseja agendar?
            </div>
            <div class="table-responsive">
              <table class="table table-bordered table-sm mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Data e hora</th>
                    <th>Profissional</th>
                    <th>Especialidade</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in duplicateContext.appointments"
                    :key="item.id_sched ?? index"
                  >
                    <td>{{ formatAppointmentDateTime(item) }}</td>
                    <td>{{ item.prof_name }}</td>
                    <td>{{ item.spec_name }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer border-top d-flex flex-column flex-sm-row gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary flex-fill"
              @click="handleDuplicateScheduleCancel"
            >
              Não, revisar agendamento
            </button>
            <button
              type="button"
              class="btn btn-primary flex-fill"
              @click="handleDuplicateScheduleConfirm"
            >
              Sim, agendar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal New/Edit Patient -->
    <div class="modal fade" id="ModalNewPatient" tabindex="-1" aria-labelledby="ModalAddress" aria-hidden="true">
      <div class="modal-dialog">
        <form method="post" @submit.prevent="handleSubmitPatient" ref="formPatient">
          <div class="modal-content rounded shadow border-0">
            <div class="modal-header border-bottom">
              <h5 class="modal-title">
                {{ isEditingPatient ? 'Editar' : 'Novo' }} paciente
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="row">
                <div class="col-md-12">
                  <div class="alert alert-success" role="alert" v-if="msgPatientSuccess">
                    {{ msgPatientSuccess }}
                  </div>

                  <div class="alert alert-danger" role="alert" v-if="msgPatientError">
                    {{ msgPatientError }}
                  </div>

                  <div class="mb-3">
                    <label class="form-label" for="patient_name">
                      Nome <span class="text-danger">*</span>
                    </label>
                    <div class="form-icon position-relative">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           class="feather feather-user fea icon-sm icons">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <input
                        name="patient_name"
                        id="patient_name"
                        type="text"
                        v-model="formPatient.patient_name"
                        :class="errors.patient_name ? 'is-invalid' : ''"
                        required
                        class="form-control ps-5"
                        placeholder="Nome :"
                      >
                      <div class="invalid-feedback d-block" v-if="errors.patient_name">
                        {{ errors.patient_name.join('\n') }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <PatientIdentityFields :form="formPatient" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label" for="patient_birthdate">
                      Data Nascimento <span class="text-danger">*</span>
                    </label>
                    <div class="form-icon position-relative">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           class="feather feather-mail fea icon-sm icons">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      <input
                        name="patient_birthdate"
                        id="patient_birthdate"
                        type="tel"
                        v-model="formPatient.patient_birthdate"
                        required
                        :class="errors.patient_birthdate ? 'is-invalid' : ''"
                        v-mask="'##/##/####'"
                        class="form-control ps-5"
                        placeholder="Data de nascimento :"
                      >
                      <div class="invalid-feedback d-block" v-if="errors.patient_birthdate">
                        {{ errors.patient_birthdate.join('\n') }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label" for="patient_gender">Sexo</label>
                    <select id="patient_gender" v-model="formPatient.patient_gender" class="form-select">
                      <option :value="null">Selecione</option>
                      <option value="Male">Masculino</option>
                      <option value="Female">Feminino</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-12">
                  <div class="mb-3">
                    <label class="form-label" for="observations">Observação</label>
                    <div class="form-icon position-relative">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           class="feather feather-message-circle fea icon-sm icons">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                      </svg>
                      <textarea
                        name="observations"
                        id="observations"
                        rows="4"
                        :class="errors.observations ? 'is-invalid' : ''"
                        v-model="formPatient.observations"
                        class="form-control ps-5"
                        placeholder="Observação do paciente :"
                      ></textarea>
                      <div class="invalid-feedback d-block" v-if="errors.observations">
                        {{ errors.observations.join('\n') }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <div class="col-md-12">
                <div class="d-grid gap-2">
                  <button type="submit" :disabled="submitted" class="btn btn-outline-primary">
                    Salvar
                    <svg
                      v-if="submitted"
                      aria-hidden="true"
                      focusable="false"
                      data-prefix="fas"
                      data-icon="spinner"
                      class="local-spin svg-inline--fa fa-spinner fa-w-16"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                    >
                      <path fill="currentColor" d="M304 48c0 26.51-21.49 48-48 48s-48-21.49-48-48 21.49-48 48-48 48 21.49 48 48zm-48 368c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zm208-208c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zM96 256c0-26.51-21.49-48-48-48S0 229.49 0 256s21.49 48 48 48 48-21.49 48-48zm12.922 99.078c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.491-48-48-48zm294.156 0c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.49-48-48-48zM108.922 60.922c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.491-48-48-48z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import PatientIdentityFields from "../../components/PatientIdentityFields.vue";
import MenuAction from './MenuAction.vue'
import http from "../../helpers/http"
import { mask } from 'vue-the-mask'
import Alert from "../../helpers/alert"
import Modal from "../../helpers/modal"
import Swal from "sweetalert2"
import date from "../../helpers/date"

const MODAL_ID = '#ModalNewPatient'
const MODAL_RETURNING_DEADLINE_ID = '#ModalReturningDeadline'
const MODAL_SELECT_RETURN_ID = '#ModalSelectReturnSchedule'
const MODAL_DUPLICATE_SCHEDULE_ID = '#ModalDuplicateSchedule'
const SWAL_CONFIRM_COLOR = '#780f5a'
const DATE_FORMAT = 'DD/MM/YYYY'
const DATETIME_FORMAT = 'DD/MM/YYYY HH:mm'

export default {
  name: 'Patient',
  components: { PatientIdentityFields,
    MenuAction,
  },
  directives: {
    mask,
  },
  data() {
    return {
      search: '',
      submitted: false,
      formPatient: this.getInitialFormPatient(),
      errors: {},
      msgPatientSuccess: '',
      msgPatientError: '',
      returningDeadlineContext: null,
      selectReturnContext: null,
      selectedReturnScheduleId: null,
      duplicateContext: null,
    }
  },
  computed: {
    sortedSelectReturnAppointments() {
      if (!this.selectReturnContext?.appointments) {
        return []
      }

      return this.sortReturnSchedules(this.selectReturnContext.appointments)
    },
    patients() {
      const patients = this.$store.state.toSchedule.patients
      if (!this.search) return patients

      const searchLower = this.search.toLowerCase()
      return patients.filter(patient => {
        const strFilter = JSON.stringify(patient).toLowerCase()
        return strFilter.includes(searchLower)
      })
    },
    paymentMethod() {
      return this.$store.state.toSchedule.schedule.paymentMethod
    },
    specialty() {
      return this.$store.state.toSchedule.schedule.specialty
    },
    professional() {
      return this.$store.state.toSchedule.schedule.professional
    },
    plan() {
      return this.$store.state.toSchedule.schedule.plan
    },
    isOnline() {
      return this.$store.state.toSchedule.attendanceType === 'ONLINE'
    },
    isEditingPatient() {
      return !!this.formPatient.id_patient
    },
    userAuth() {
      return this.$store.state.auth.user
    },
  },
  mounted() {
    this.$store.dispatch('toSchedule/getPatients')
  },
  methods: {
    date,

    getInitialFormPatient() {
      return {
        patient_name: '',
        patient_cpf: '',
        patient_birthdate: '',
        patient_gender: null,
        observations: '',
      }
    },

    resetFormPatient() {
      this.formPatient = this.getInitialFormPatient()
      this.errors = {}
      this.msgPatientSuccess = ''
      this.msgPatientError = ''
    },

    openNewPatientModal() {
      this.resetFormPatient()
      Modal.toggle(MODAL_ID)
    },

    openEditPatientModal(patient) {
      this.resetFormPatient()
      this.formPatient = {
        id_patient: patient.id_patient,
        patient_name: patient.patient_name,
        ...Object.fromEntries(['is_foreign', 'nationality', 'document_type', 'document_country', 'document_number'].map(key => [key, patient[key]])),
        patient_cpf: patient.patient_cpf,
        patient_birthdate: patient.patient_birthdate_br,
        patient_gender: patient.patient_gender ?? null,
        observations: patient.observations || '',
      }
      Modal.toggle(MODAL_ID)
    },

    handleSubmitPatient() {
      if (this.isEditingPatient) {
        this.updatePatient()
      } else {
        this.storePatient()
      }
    },

    async storePatient() {
      this.submitted = true
      this.clearMessages()
      this.errors = {}

      const payload = { ...this.formPatient }
      delete payload.id_patient

      try {
        await http.post(`/users/${this.userAuth.id_user}/patients`, payload)
        this.msgPatientSuccess = 'Paciente cadastrado com sucesso.'
        this.$store.dispatch('toSchedule/getPatients')
        this.resetFormPatient()
        Modal.toggle(MODAL_ID)
      } catch (error) {
        this.handlePatientError(error)
      } finally {
        this.submitted = false
      }
    },

    async updatePatient() {
      this.submitted = true
      this.clearMessages()
      this.errors = {}

      try {
        await http.put(
          `/users/${this.userAuth.id_user}/patients/${this.formPatient.id_patient}`,
          this.formPatient
        )
        this.msgPatientSuccess = 'Paciente atualizado com sucesso.'
        this.$store.dispatch('toSchedule/getPatients')
        this.resetFormPatient()
        Modal.toggle(MODAL_ID)
      } catch (error) {
        this.handlePatientError(error)
      } finally {
        this.submitted = false
      }
    },

    handlePatientError(error) {
      const response = error.response
      if (response?.status === 422) {
        this.errors = response.data.errors || {}
      } else {
        this.msgPatientError = response?.data?.message || response?.data || 'Erro ao processar solicitação.'
      }
    },

    clearMessages() {
      this.msgPatientSuccess = ''
      this.msgPatientError = ''
    },

    async handlePatientClick(patient) {
      if (!patient.patient_age) {
        await this.requestBirthdateAndSave(patient)
        return
      }
      await this.validateAndProceedWithPatient(patient)
    },

    async requestBirthdateAndSave(patient) {
      const result = await Swal.fire({
        text: 'Qual sua data de nascimento?',
        html: `<input type="date" id="patient_birthdate_swal" class="swal2-input" placeholder="DD/MM/YYYY">`,
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        confirmButtonColor: SWAL_CONFIRM_COLOR,
        cancelButtonText: 'Fechar',
        focusConfirm: false,
        preConfirm: () => {
          const dateInput = document.getElementById('patient_birthdate_swal')
          if (!dateInput?.value) {
            Swal.showValidationMessage('Por favor, informe a data de nascimento.')
            return null
          }
          return { date: dateInput.value }
        }
      })

      if (!result.isConfirmed) return

      const { date: birthdate } = result.value
      const formattedBirthdate = this.date(birthdate).format(DATE_FORMAT)

      try {
        await this.savePatientBirthdate(patient, formattedBirthdate)
        await this.validateAndProceedWithPatient(patient)
      } catch (error) {
        Alert.error('Ops, falha ao salvar data de nascimento.')
      }
    },

    async savePatientBirthdate(patient, birthdate) {
      const payload = {
        id_patient: patient.id_patient,
        patient_name: patient.patient_name,
        ...Object.fromEntries(['is_foreign', 'nationality', 'document_type', 'document_country', 'document_number'].map(key => [key, patient[key]])),
        patient_cpf: patient.patient_cpf,
        patient_birthdate: birthdate,
        observations: patient.observations || '',
      }

      await http.put(
        `/users/${this.userAuth.id_user}/patients/${patient.id_patient}`,
        payload
      )
    },

    async validateAndProceedWithPatient(patient) {
      await this.checkReturningDeadline(patient)
    },

    async checkReturningDeadline(patient) {
      try {
        const response = await http.get('/schedule/appointment/check-returning-deadline', {
          params: {
            ...Object.fromEntries(['is_foreign', 'nationality', 'document_type', 'document_country', 'document_number'].map(key => [key, patient[key]])),
        patient_cpf: patient.patient_cpf,
            id_spec: this.specialty.id_spec,
            id_prof: this.professional.id_prof,
            id_place: this.professional.id_place,
            sched_online: this.isOnline,
          }
        })

        if (response.data?.length > 0) {
          const outcome = await this.showReturningDeadlineWarning(patient, response.data)

          if (outcome.action === 'return') {
            if (!outcome.schedule?.id_sched) {
              Alert.error('Não foi possível identificar a consulta para o retorno. Tente novamente.')
              return
            }

            this.$router.push({
              path: `/schedules/${outcome.schedule.id_sched}/return`,
              query: { from: 'scheduling' },
            })
            return
          }

          if (outcome.action === 'continue') {
            await this.checkDuplicateSchedule(patient)
            return
          }

          return
        }

        await this.checkDuplicateSchedule(patient)
      } catch (error) {
        console.error('Error checking returning deadline:', error)
        await this.checkDuplicateSchedule(patient)
      }
    },

    async showReturningDeadlineWarning(patient, appointments) {
      while (true) {
        const choice = await this.openReturningDeadlineModal(patient, appointments)

        if (choice.action === 'continue') {
          return { action: 'continue' }
        }

        if (choice.action === 'cancel') {
          return { action: 'cancel' }
        }

        if (appointments.length === 1) {
          return { action: 'return', schedule: appointments[0] }
        }

        const selection = await this.openSelectReturnModal(patient, appointments)

        if (selection.action === 'return') {
          return selection
        }
      }
    },

    openReturningDeadlineModal(patient, appointments) {
      return new Promise((resolve) => {
        this.returningDeadlineContext = { patient, appointments, resolve }
        Modal.toggle(MODAL_RETURNING_DEADLINE_ID)
      })
    },

    openSelectReturnModal(patient, appointments) {
      return new Promise((resolve) => {
        const sortedAppointments = this.sortReturnSchedules(appointments)
        this.selectReturnContext = { patient, appointments, resolve }
        this.selectedReturnScheduleId = sortedAppointments[0]?.id_sched ?? null
        Modal.toggle(MODAL_SELECT_RETURN_ID)
      })
    },

    handleReturningDeadlineReturn() {
      const context = this.returningDeadlineContext
      if (!context) {
        return
      }

      Modal.toggle(MODAL_RETURNING_DEADLINE_ID)
      context.resolve({ action: 'return' })
      this.returningDeadlineContext = null
    },

    handleReturningDeadlineContinue() {
      const context = this.returningDeadlineContext
      if (!context) {
        return
      }

      Modal.toggle(MODAL_RETURNING_DEADLINE_ID)
      context.resolve({ action: 'continue' })
      this.returningDeadlineContext = null
    },

    handleSelectReturnConfirm() {
      const context = this.selectReturnContext
      if (!context) {
        return
      }

      if (!this.selectedReturnScheduleId) {
        Alert.error('Selecione uma consulta.')
        return
      }

      const schedule = context.appointments.find(
        (item) => String(item.id_sched) === String(this.selectedReturnScheduleId)
      )

      if (!schedule) {
        Alert.error('Consulta selecionada inválida. Tente novamente.')
        return
      }

      Modal.toggle(MODAL_SELECT_RETURN_ID)
      context.resolve({ action: 'return', schedule })
      this.selectReturnContext = null
      this.selectedReturnScheduleId = null
    },

    handleSelectReturnBack() {
      const context = this.selectReturnContext
      if (!context) {
        return
      }

      Modal.toggle(MODAL_SELECT_RETURN_ID)
      context.resolve({ action: 'back' })
      this.selectReturnContext = null
      this.selectedReturnScheduleId = null
    },

    formatAppointmentDateTime(appointment) {
      return this.date(this.getAppointmentDateTime(appointment)).format(DATETIME_FORMAT)
    },

    async checkDuplicateSchedule(patient) {
      this.validatePatientAge(patient)

      try {
        const response = await http.get('/schedule/appointment/check', {
          params: {
            ...Object.fromEntries(['is_foreign', 'nationality', 'document_type', 'document_country', 'document_number'].map(key => [key, patient[key]])),
        patient_cpf: patient.patient_cpf,
            id_spec: this.specialty.id_spec,
            id_prof: this.professional.id_prof,
            id_place: this.professional.id_place,
            sched_online: this.isOnline,
          }
        })

        if (response.data?.length > 0) {
          const shouldProceed = await this.showDuplicateScheduleWarning(patient, response.data)
          if (shouldProceed) {
            await this.proceedToPayment(patient)
          }
        } else {
          await this.proceedToPayment(patient)
        }
      } catch (error) {
        console.error('Error checking duplicate schedule:', error)
        Alert.error('Erro ao verificar agendamentos. Tente novamente.')
      }
    },

    validatePatientAge(patient) {
      const professional = this.professional
      const patientAge = parseInt(patient.patient_age)
      const minAge = parseInt(professional.min_patient_age ?? 0)
      const maxAge = parseInt(professional.max_patient_age ?? 999)

      if (patientAge < minAge || patientAge > maxAge) {
        const ageRange = professional.patient_age_desc || `${minAge} a ${maxAge} anos`
        Alert.error(
          `Desculpe, mas o profissional não atende pacientes dessa idade.\n` +
          `Faixa etária permitida: ${ageRange}`
        )
        throw new Error('Patient age out of range')
      }
    },

    showDuplicateScheduleWarning(patient, appointments) {
      return new Promise((resolve) => {
        this.duplicateContext = { patient, appointments, resolve }
        Modal.toggle(MODAL_DUPLICATE_SCHEDULE_ID)
      })
    },

    handleDuplicateScheduleConfirm() {
      const context = this.duplicateContext
      if (!context) {
        return
      }

      Modal.toggle(MODAL_DUPLICATE_SCHEDULE_ID)
      context.resolve(true)
      this.duplicateContext = null
    },

    handleDuplicateScheduleCancel() {
      const context = this.duplicateContext
      if (!context) {
        return
      }

      Modal.toggle(MODAL_DUPLICATE_SCHEDULE_ID)
      context.resolve(false)
      this.duplicateContext = null
    },

    sortReturnSchedules(appointments) {
      return [...appointments].sort((a, b) => {
        const dateA = a.schedule_date_time ?? `${a.sched_date}T${a.sched_time}`
        const dateB = b.schedule_date_time ?? `${b.sched_date}T${b.sched_time}`

        return this.date(dateB).valueOf() - this.date(dateA).valueOf()
      })
    },

    getAppointmentDateTime(appointment) {
      return appointment.schedule_date_time ?? `${appointment.sched_date}T${appointment.sched_time}`
    },

    async proceedToPayment(patient) {
      if (this.plan?.id_plan) {
        const isValid = await this.validatePlanMember(patient.patient_cpf, this.plan)
        if (!isValid) return
      }

      this.$store.commit('toSchedule/selectPatient', patient)
      this.$router.push('/payment')
    },

    async validatePlanMember(patientCpf, plan) {
      try {
        const response = await http.get('schedule/plans/validate-plan-member', {
          params: {
            patient_plan_cpf: patientCpf,
            id_plan: plan.id_plan,
          }
        })

        if (response.data.status !== 'success') {
          Alert.error(response.data.message || 'Erro ao validar assinante do plano.')
          return false
        }

        return true
      } catch (error) {
        const errorMessage = error.response?.data?.message ||
          'Ocorreu um erro ao validar o assinante. Por favor, tente novamente.'
        Alert.error(errorMessage)
        return false
      }
    },
  }
}
</script>

<style scoped>
</style>
